import { defineStore } from 'pinia'
import { nextTick, watch, watchEffect } from 'vue'

import {
  findDocumentForFailedAccountPill,
  findPendingUploadForPill,
  getFailedAccountStatementPills,
} from '~/libs/document-helper'
import { sessionStorageService } from '~/libs/session-storage.service'
import { useAppAnalyticsStore } from '~/store/app-analytics'
import { useApplicationConfigStore } from '~/store/application-config'
import { useBankConnectorStore } from '~/store/bank-connector'
import { useBorrowerStore } from '~/store/borrower'
import { useDocumentsStore } from '~/store/documents'
import { useRootStore } from '~/store/root'

const HOLD_DURATION_MS = 20000

export const useBankPullStatusUiStore = defineStore('bankPullStatusUi', () => {
  const appAnalyticsStore = useAppAnalyticsStore()
  const applicationConfigStore = useApplicationConfigStore()
  const bankConnectorStore = useBankConnectorStore()
  const borrowerStore = useBorrowerStore()
  const documentsStore = useDocumentsStore()
  const rootStore = useRootStore()
  const currentRoute = useRoute()

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
  */

  // Latches true the first time indicatorState is observed as 'pending' and never resets within
  // the page session, so a returning borrower whose pull already completed does not see a chip
  // pop in and vanish on every load.
  const hasObservedPending = ref(false)

  // A counter, not a boolean, because breakpoint-exclusive chip instances (portal desktop +
  // portal mobile, for example) both render under jsdom and must not desynchronise pause/resume.
  const hoverCount = ref(0)

  // True once the 20s post-completion hold has run out with no hover in progress.
  const holdExpired = ref(false)

  // Latches true once the chip's one-shot entry-attention pulse has been shown this session, so
  // a second breakpoint-exclusive instance mounting in the same tick - or any later appearance -
  // never replays it.
  const hasShownAttention = ref(false)

  // Latches true once the borrower's documents have loaded, and is cleared on a borrower switch.
  // documentsStore.loaded itself drops to false for the length of every getDocuments() refresh
  // (Pusher, bank sync, the missing-statements banner), which would switch the failed-account red
  // state and counts off and back on each time.
  const documentsLoadedOnce = ref(false)

  // Shared across every mounted BankPullFailureToastHost instance (portal/marketplace/embedded
  // each mount their own), the same way hoverCount is shared across chip instances above - the
  // toast group is a singleton concept, so "has the failure toast already been added" cannot be
  // tracked per-component-instance without every instance independently adding a duplicate.
  const failureToastShown = ref(false)

  // Not reactive state anything else depends on, so plain Sets (like holdTimer below) rather than
  // refs. failureToastHosts holds the ids of host instances that are mounted. failureToastHolders
  // is the host that is actually rendering the toast: each host renders its own <wa-toast-item>,
  // so only the instance whose markFailureToastShown() call claimed the flag shows it. Every other
  // host - co-mounted or mounted later (e.g. the incoming layout during a swap) - holds nothing.
  const failureToastHosts = new Set()
  let failureToastHolders = new Set()

  let holdTimer = null

  // Set once the one-time documents fetch for failed-account statements has been requested;
  // cleared on a borrower switch.
  let documentsFetchRequested = false

  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
  */

  // The single prompt-eligibility chain shared by the banner, popover, and failure toast, so
  // none of them can disagree about whether the borrower should be told about a failed pull.
  // Formerly duplicated inside useMissingStatementsBanner.js. Approvals are deliberately not a
  // guard here: an approved borrower can still have a real, unresolved statement pull failure
  // that needs surfacing.
  const promptEligible = computed(() => {
    if (!borrowerStore.mpApplicationComplete) return false
    if (documentsStore.hasRequiredBankStatementsManual) return false
    return true
  })

  // Upload slots for the months bank-service failed to retrieve, one group per connected checking
  // account that can pull statements. Built from pull status and accounts only - never from
  // documents or pending uploads - so a slot's identity does not change while an upload into it
  // is in flight. Absent or degraded status (no failedMonths) yields no groups.
  const _failedAccountSlotGroups = computed(() => {
    const accountStatuses = bankConnectorStore.statementPullStatus?.accountStatuses
    if (!accountStatuses) return []

    return bankConnectorStore.normalizedCheckingAccounts
      .filter(account => account.providerStatements !== false)
      .map(account => {
        const accountStatus = accountStatuses[account.accountId]
        if (!(accountStatus?.failed > 0)) return null
        const slots = getFailedAccountStatementPills(account.accountId, accountStatus.failedMonths)
        return slots.length ? { account, slots } : null
      })
      .filter(Boolean)
  })

  // A slot is satisfied by a finished bank statement for the same account and month. An upload
  // still in progress or errored leaves it unsatisfied, mirroring how documentsStore maps month
  // requirements.
  const failedAccountGroups = computed(() => _failedAccountSlotGroups.value.map(({ account, slots }) => {
    const mappedSlots = slots.map(slot => {
      const pendingUpload = findPendingUploadForPill(documentsStore.pendingUploads ?? [], slot)
      const finishedDocument = findDocumentForFailedAccountPill(documentsStore.documents ?? [], slot)
      return {
        ...slot,
        document: pendingUpload && !pendingUpload.error ? { filename: pendingUpload.filenames[0] } : finishedDocument,
        progress: pendingUpload ? pendingUpload.progress : null,
        error: pendingUpload ? pendingUpload.error : null,
        satisfied: !pendingUpload && Boolean(finishedDocument),
      }
    })
    return {
      account,
      slots: mappedSlots,
      needed: mappedSlots.filter(slot => !slot.satisfied).length,
    }
  }))

  const additionalStatementsNeeded = computed(() => {
    return failedAccountGroups.value.reduce((total, group) => total + group.needed, 0)
  })

  // Whether failed-account statements can be acted on here, shared by the BR-5 clause and the count
  // copy so the two never disagree. Portal routes upload without an account tag, so neither a red
  // state nor a count there could ever clear; documents must have loaded so a stale, empty document
  // list never reports statements as missing.
  const _failedAccountUploadAvailable = computed(() => {
    if (applicationConfigStore.currentModule?.disableManualBankStatement) return false
    if (!currentRoute.path?.includes('/application')) return false
    return documentsLoadedOnce.value
  })

  // Per-account count copy for the popover, toast, and connected-accounts row: accountId ->
  // statements still needed, 0 once that account's slots are all filled (BR-11). Empty - so every
  // surface keeps today's copy - while the flag is off or failed-account upload is unavailable.
  const statementsNeededByAccountId = computed(() => {
    if (!applicationConfigStore.isBankPullStatusIndicatorEnabled) return {}
    if (!_failedAccountUploadAvailable.value) return {}
    return Object.fromEntries(failedAccountGroups.value.map(group => [group.account.accountId, group.needed]))
  })

  const statementsNeededTotal = computed(() => {
    return Object.values(statementsNeededByAccountId.value).reduce((total, needed) => total + needed, 0)
  })

  // The bank-details "Retrieving recent statements" row. Follows statementPullRetrievalState, except
  // that when bank-service reports the pull failed, a failed account whose missing statements are
  // all uploaded counts as completed, as its own row does (BR-11) - so the row clears once no
  // account needs anything, and stays pending while another account is still pulling. Any other
  // cause of failure (a completed pull missing required months, no account able to pull) stands,
  // as does any failed account without a count of 0: one no longer connected, or every account
  // while counts are off (flag off, upload unavailable).
  const progressRetrievalState = computed(() => {
    const retrievalState = bankConnectorStore.statementPullRetrievalState
    if (retrievalState !== 'failed' || bankConnectorStore.statementPullStatus?.status !== 'failed') return retrievalState

    const accountStatuses = bankConnectorStore.statementPullStatus.accountStatuses ?? {}
    const failedAccountIds = Object.keys(accountStatuses).filter(accountId => accountStatuses[accountId]?.failed > 0)
    if (failedAccountIds.length === 0 || failedAccountIds.some(accountId => statementsNeededByAccountId.value[accountId] !== 0)) {
      return retrievalState
    }

    const accountStates = bankConnectorStore.normalizedCheckingAccounts
      .filter(account => account.providerStatements !== false)
      .map(account => {
        const pullState = bankConnectorStore.getAccountPullState(account.accountId)
        return pullState === 'failed' && statementsNeededByAccountId.value[account.accountId] === 0 ? 'completed' : pullState
      })
      .filter(state => state !== null)

    if (accountStates.length === 0 || accountStates.includes('failed')) return retrievalState
    return accountStates.includes('pending') ? 'pending' : 'completed'
  })

  // BR-5: statements still missing for a failed account turn the indicator red even when today's
  // month slots are full - but only where the borrower can act on it.
  const _failedAccountStatementsMissing = computed(() => {
    if (!borrowerStore.mpApplicationComplete) return false
    if (!_failedAccountUploadAvailable.value) return false
    return additionalStatementsNeeded.value > 0
  })

  // null means "render nothing" - never inferred from an absent pull-status result. pending and
  // completed pass through from the shared display derivation; failed only surfaces once the
  // shared prompt guards pass, so a manual upload can clear it even though
  // statementPullRetrievalState itself stays pinned to 'failed'.
  const indicatorState = computed(() => {
    // No connected checking accounts at all (e.g. the borrower just deleted their only one):
    // there is nothing to report, whatever stale statementPullStatus or document state is left
    // over from before the deletion - without this the chip/popover/toast read 'failed' or
    // 'completed' ("All accounts connected") for an account that no longer exists. Kept here, not
    // in statementPullRetrievalState: that derivation also drives the existing bank-details
    // progress row and its analytics event, so changing it would alter shipped behaviour there.
    if (bankConnectorStore.normalizedCheckingAccounts.length === 0) return null
    if (_failedAccountStatementsMissing.value) return 'failed'

    const retrievalState = bankConnectorStore.statementPullRetrievalState

    if (retrievalState === 'pending') return 'pending'
    if (retrievalState === 'completed') return 'completed'
    if (retrievalState === 'failed') return promptEligible.value ? 'failed' : null
    if (retrievalState != null) {
      log?.warning(`bank-pull-status-ui: unrecognized statementPullRetrievalState "${retrievalState}"`)
    }
    return null
  })

  const visible = computed(() => {
    if (!applicationConfigStore.isBankPullStatusIndicatorEnabled) return false

    const state = indicatorState.value
    if (state === 'pending' || state === 'failed') return true
    if (state === 'completed') return hasObservedPending.value && !holdExpired.value
    return false
  })

  // True only for the chip's first-ever visible appearance this session. The store itself commits
  // hasShownAttention on a deferred tick (see the watcher below), not the instant this flips true,
  // so every chip instance mounted or reacting in the same tick - including ones that mount long
  // before the pull ever starts - observes the identical pre-commit value before any of them latch
  // their own local one-way "should I animate" flag from it.
  const showAttentionPulse = computed(() => visible.value && !hasShownAttention.value)

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
  */

  function _clearHoldTimer() {
    clearTimeout(holdTimer)
    holdTimer = null
  }

  function _startHoldTimer() {
    _clearHoldTimer()
    holdTimer = setTimeout(() => {
      holdExpired.value = true
      holdTimer = null
    }, HOLD_DURATION_MS)
  }

  function startHover() {
    hoverCount.value += 1
  }

  function endHover() {
    hoverCount.value = Math.max(0, hoverCount.value - 1)
  }

  // Returns true only when this call is the one that actually transitions the shared flag -
  // the caller should only render the toast in that case, and must do so right away from a
  // mounted host, passing its own id: that host is recorded as the one showing it. A second host
  // instance observing the same ongoing failure sees this already true and does nothing, so only
  // one toast is ever on screen.
  function markFailureToastShown(hostId) {
    if (failureToastShown.value) return false
    failureToastShown.value = true
    failureToastHolders = new Set(hostId === undefined ? [] : [hostId])
    return true
  }

  function markFailureToastHidden() {
    if (!failureToastShown.value) return false
    failureToastShown.value = false
    failureToastHolders = new Set()
    return true
  }

  // Called from a host's onMounted.
  function registerFailureToastHost(hostId) {
    failureToastHosts.add(hostId)
  }

  // Each host instance calls this on its own unmount. If the departing instance was the one showing
  // the toast (e.g. an outgoing layout during a swap), the flag is cleared here so a surviving
  // host's live watcher raises the toast again in its own stack; leaving the flag set would make
  // it believe the toast was still on screen. Returns true when the flag was cleared.
  function unregisterFailureToastHost(hostId) {
    failureToastHosts.delete(hostId)
    failureToastHolders.delete(hostId)
    if (failureToastHolders.size > 0) return false
    return markFailureToastHidden()
  }

  function _markAttentionShown() {
    hasShownAttention.value = true
  }

  function reset() {
    hasObservedPending.value = false
    holdExpired.value = false
    hoverCount.value = 0
    hasShownAttention.value = false
    _clearHoldTimer()
    _syncHoldTimer()
  }

  // Single source of truth for whether the hold timer should be running, so reset() and the
  // watchEffect below can never apply this decision differently from one another.
  function _syncHoldTimer() {
    if (indicatorState.value !== 'completed' || !hasObservedPending.value) {
      holdExpired.value = false
      _clearHoldTimer()
      return
    }

    if (hoverCount.value === 0) {
      // Idempotency guard: this watchEffect intentionally re-runs on ANY change to
      // statementPullStatus/normalizedCheckingAccounts (see the comment below), including a new
      // array/object reference with unchanged data (e.g. every getBanks() resolution, which the
      // portal layout and dashboard both call on their own, independent of any real pull-status
      // change). Without this guard, each such re-run restarted the 20s hold from zero, so the
      // chip could never actually reach "held long enough" on pages that re-fetch banks more than
      // once - only a hard refresh (which resets hasObservedPending) hid it. Only start a fresh
      // timer on a genuine transition into this state (no timer running yet, not already expired).
      if (holdTimer === null && !holdExpired.value) _startHoldTimer()
    } else {
      _clearHoldTimer()
    }
  }

  // watchEffect (not watch) deliberately: it re-runs on ANY reactive property it touches
  // recomputing, not just on indicatorState's memoized OUTPUT changing. That matters here because
  // bank-connector has no borrowerId watcher of its own, so a borrower switch that lands on the
  // same label the outgoing borrower had (e.g. both 'pending') must still re-evaluate once the
  // new borrower's own statementPullStatus/normalizedCheckingAccounts actually lands, even though
  // indicatorState's derived string never changes value across that refetch. Also folds in what
  // used to be a separate hoverCount watch (a second watch calling the same _syncHoldTimer) into
  // this one effect, since both exist purely to keep the hold timer in sync.
  watchEffect(() => {
    if (indicatorState.value === 'pending') hasObservedPending.value = true
    void bankConnectorStore.statementPullStatus
    void bankConnectorStore.normalizedCheckingAccounts
    void hoverCount.value
    _syncHoldTimer()
  })

  // Commits the one-shot attention latch on a deferred tick (rather than the instant
  // showAttentionPulse flips true) so every chip instance's own immediate watch on
  // showAttentionPulse - including ones mounted long before this - observes the same
  // pre-commit value in the same synchronous pass, before any of them latch their local
  // one-way "should I animate" flag from it.
  watch(showAttentionPulse, (shouldPulse) => {
    if (shouldPulse) nextTick(_markAttentionShown)
  }, { immediate: true })

  // A borrower switching business must not see the previous business's timing/visibility state.
  // previousId is null on the very first hydration (logged-out -> logged-in), which must not
  // count as a "switch" - re-hydrating bank-connector data itself for the new borrower is
  // handled separately.
  watch(() => borrowerStore.borrowerId, (id, previousId) => {
    if (previousId != null && id !== previousId) {
      documentsFetchRequested = false
      documentsLoadedOnce.value = false
      reset()
    }
  })

  watch(() => documentsStore.loaded, (loaded) => {
    if (loaded) documentsLoadedOnce.value = true
  }, { immediate: true, flush: 'sync' })

  // The popover's per-account rows read normalizedCheckingAccounts, which otherwise stays empty
  // unless the borrower happens to have visited the bank-statements module this session. Loaded
  // here, only once the flag is on, rather than by the layouts for every borrower: an early
  // getBanks() changes the bank-statements module's own first load (it skips its fetch and
  // setDocumentsFastFlow() when banks are already present), which must not happen outside the
  // experiment. Waits for borrowerId too, since the flag can resolve before the borrower loads.
  // A borrower switch is left to bank-connector's own borrowerId watcher, which refetches banks.
  // Silent, so a failed request never raises the bank-connect flow's "sync services" error.
  watch(
    () => applicationConfigStore.isBankPullStatusIndicatorEnabled && borrowerStore.borrowerId != null,
    (shouldLoadBanks) => {
      if (shouldLoadBanks) bankConnectorStore.getBanks(false, { silent: true })
    },
    { immediate: true },
  )

  // Off the documents step, documents are often never fetched, which would hold the BR-5 clause
  // off for the whole session. Fetched once per borrower when failed-month data first needs them.
  watch(
    () => applicationConfigStore.isBankPullStatusIndicatorEnabled
      && _failedAccountSlotGroups.value.length > 0
      && !documentsStore.loaded,
    (shouldFetch) => {
      if (!shouldFetch || documentsFetchRequested) return
      documentsFetchRequested = true
      Promise.resolve(documentsStore.getDocuments()).catch((err) => {
        log?.error('bank-pull-status-ui: failed to load documents for failed-account statements', { err })
      })
    },
    { immediate: true },
  )

  function _analyticsPage() {
    if (rootStore.isEmbedded) return 'embedded'
    return currentRoute.path?.split('/')[1] === 'portal' ? 'portal' : 'application'
  }

  // Sent from the store rather than the chip, since most layouts mount two breakpoint-exclusive
  // chip instances that would each send it. Once per borrower, per state, per session, matching
  // the 'Statement Pull Status' event's sessionStorage dedupe but scoped by borrower like the
  // toast dismissal. Keyed on visible, so it only fires for borrowers in the experiment.
  watch(
    () => (visible.value ? indicatorState.value : null),
    (state) => {
      const borrowerId = borrowerStore.borrowerId
      if (!state || !borrowerId) return
      const key = `fs_bank_pull_status_indicator_shown_${borrowerId}_${state}`
      if (sessionStorageService.getItem(key)) return
      sessionStorageService.setItem(key, '1')
      appAnalyticsStore.fsTrackEvent({
        name: 'Bank Pull Status Indicator Shown',
        properties: {
          state,
          page: _analyticsPage(),
          accountCount: bankConnectorStore.normalizedCheckingAccounts.length,
        },
      })
    },
    { immediate: true },
  )

  return {
    // STATE
    failureToastShown,
    hasObservedPending,
    hasShownAttention,
    holdExpired,
    hoverCount,

    // GETTERS
    additionalStatementsNeeded,
    failedAccountGroups,
    indicatorState,
    progressRetrievalState,
    promptEligible,
    showAttentionPulse,
    statementsNeededByAccountId,
    statementsNeededTotal,
    visible,

    // ACTIONS
    endHover,
    markFailureToastHidden,
    markFailureToastShown,
    registerFailureToastHost,
    reset,
    startHover,
    unregisterFailureToastHost,
  }
})
